import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { fetchSummary, fetchAccount, setInitialCapital } from '../api/summary';
import { fetchDailyPnl } from '../api/autoTrade';
import SummaryCards from '../components/SummaryCards';
import EquityChart from '../components/EquityChart';
import { getPriceColor, formatPct, formatMoney, formatNumber, formatDateTime } from '../utils/format';
import type { InvestmentSummary, DailyPnl, StockSellDetail } from '../types';

/**
 * 投资总结页
 * 概览卡片 + 交易明细 + 净值曲线
 * 支持按时间段查看
 */
export default function SummaryPage() {
  const [period, setPeriod] = useState('all');
  const [summary, setSummary] = useState<InvestmentSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [dailyPnlList, setDailyPnlList] = useState<DailyPnl[]>([]);
  const [detailFilter, setDetailFilter] = useState<'all' | 'up' | 'down'>('all');
  // 本金设定（危险操作：会清空全部持仓/成交/流水）
  const [account, setAccount] = useState<{ initialCapital: number; balance: number } | null>(null);
  const [capitalOpen, setCapitalOpen] = useState(false);
  const [capitalInput, setCapitalInput] = useState('');
  const [savingCapital, setSavingCapital] = useState(false);

  useEffect(() => {
    fetchAccount()
      .then(setAccount)
      .catch((e) => console.error('Failed to load account:', e));
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchSummary(period);
        setSummary(data);
      } catch (error) {
        console.error('获取总结失败:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period]);

  useEffect(() => {
    fetchDailyPnl(30)
      .then(setDailyPnlList)
      .catch((e) => console.error('Failed to load daily P&L:', e));
  }, [summary]);

  const stockDetails = summary?.stockDetails ?? [];
  const filteredDetails =
    detailFilter === 'all' ? stockDetails : stockDetails.filter((d) => d.sellType === detailFilter);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          投资总结
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => {
              setCapitalInput(String(account?.initialCapital ?? 1000000));
              setCapitalOpen(true);
            }}
          >
            本金设定
          </Button>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(_e, val) => val && setPeriod(val)}
            size="small"
          >
          <ToggleButton value="today">今日</ToggleButton>
          <ToggleButton value="week">本周</ToggleButton>
          <ToggleButton value="month">本月</ToggleButton>
          <ToggleButton value="all">全部</ToggleButton>
        </ToggleButtonGroup>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : summary ? (
        <>
          {/* 概览卡片 */}
          <SummaryCards summary={summary} />

          {/* 净值曲线 */}
          {summary.equityCurve && summary.equityCurve.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                净值曲线
              </Typography>
              <EquityChart data={summary.equityCurve} />
            </Paper>
          )}

          {/* 交易明细 */}
          <Paper sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
              交易明细（{summary.tradeCount} 笔）
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>时间</TableCell>
                    <TableCell>代码</TableCell>
                    <TableCell>名称</TableCell>
                    <TableCell align="center">方向</TableCell>
                    <TableCell align="right">价格</TableCell>
                    <TableCell align="right">数量</TableCell>
                    <TableCell align="right">金额</TableCell>
                    <TableCell align="right">费用</TableCell>
                    <TableCell align="right">盈亏</TableCell>
                    <TableCell>原因</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.trades.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          暂无交易记录
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.trades.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{formatDateTime(t.tradeTime)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{t.code}</TableCell>
                        <TableCell>{t.name}</TableCell>
                        <TableCell align="center">
                          <span style={{ color: t.direction === 'buy' ? '#f44336' : '#4caf50', fontWeight: 600 }}>
                            {t.direction === 'buy' ? '买入' : '卖出'}
                          </span>
                        </TableCell>
                        <TableCell align="right">{formatNumber(t.price)}</TableCell>
                        <TableCell align="right">{t.quantity}</TableCell>
                        <TableCell align="right">{formatMoney(t.amount)}</TableCell>
                        <TableCell align="right" sx={{ color: t.fee ? 'text.secondary' : 'text.disabled' }}>
                          {t.fee !== undefined && t.fee > 0 ? formatMoney(t.fee) : '--'}
                        </TableCell>
                        <TableCell align="right">
                          {t.profit !== undefined ? (
                            <span style={{ color: getPriceColor(t.profit), fontWeight: 600 }}>
                              {formatMoney(t.profit)}
                              {t.profitPct !== undefined && (
                                <span style={{ fontSize: 12 }}>
                                  {' '}({formatPct(t.profitPct)})
                                </span>
                              )}
                            </span>
                          ) : (
                            '--'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {t.reason || ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* 入选股票明细 */}
          {summary.stockDetails && (
            <Paper sx={{ mt: 2 }}>
              <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  入选股票明细（{summary.stockDetails.length} 只）
                </Typography>
                <ToggleButtonGroup
                  value={detailFilter}
                  exclusive
                  onChange={(_e, val) => val && setDetailFilter(val)}
                  size="small"
                >
                  <ToggleButton value="all">全部</ToggleButton>
                  <ToggleButton value="up">上涨卖出</ToggleButton>
                  <ToggleButton value="down">下跌卖出</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* 卖出盈亏总结 */}
              {summary.sellSummary && summary.stockDetails.length > 0 && (
                <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={`上涨卖出 ${summary.sellSummary.upCount} 笔 · 盈利 ${formatMoney(summary.sellSummary.upProfit)}`}
                    sx={{ bgcolor: '#ffebee', color: '#f44336', fontWeight: 600 }}
                  />
                  <Chip
                    size="small"
                    label={`下跌卖出 ${summary.sellSummary.downCount} 笔 · 亏损 ${formatMoney(Math.abs(summary.sellSummary.downProfit))}`}
                    sx={{ bgcolor: '#e8f5e9', color: '#4caf50', fontWeight: 600 }}
                  />
                  {summary.sellSummary.manualCount > 0 && (
                    <Chip
                      size="small"
                      label={`手动卖出 ${summary.sellSummary.manualCount} 笔 · 盈亏 ${formatMoney(summary.sellSummary.manualProfit)}`}
                      sx={{ bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 600 }}
                    />
                  )}
                </Box>
              )}

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1300 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>代码</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>名称</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>买入日期</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>买入价</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>入选收盘价</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>卖出日期</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>卖出价</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>卖出类型</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>盈亏额</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>盈亏%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>今日行情</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>当时入选条件</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            暂无入选股票明细
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDetails.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{d.code}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.displayName || d.name}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{d.buyDate}</TableCell>
                          <TableCell align="right">{formatNumber(d.buyPrice)}</TableCell>
                          <TableCell align="right">
                            {d.candidatePrice !== undefined ? (
                              <span title={d.candidateDate ? `入选日 ${d.candidateDate}` : ''}>
                                {formatNumber(d.candidatePrice)}
                              </span>
                            ) : '--'}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{d.sellDate || '--'}</TableCell>
                          <TableCell align="right">{formatNumber(d.sellPrice)}</TableCell>
                          <TableCell align="center">
                            {d.sellType === 'up' && (
                              <Chip size="small" label="上涨卖出" sx={{ bgcolor: '#ffebee', color: '#f44336', fontWeight: 600 }} />
                            )}
                            {d.sellType === 'down' && (
                              <Chip size="small" label="下跌卖出" sx={{ bgcolor: '#e8f5e9', color: '#4caf50', fontWeight: 600 }} />
                            )}
                            {d.sellType === 'manual' && (
                              <Chip size="small" label="手动卖出" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {d.profit !== undefined ? (
                              <span style={{ color: getPriceColor(d.profit), fontWeight: 600 }}>
                                {formatMoney(d.profit)}
                              </span>
                            ) : '--'}
                          </TableCell>
                          <TableCell align="right">
                            {d.profitPct !== undefined ? (
                              <span style={{ color: getPriceColor(d.profitPct), fontWeight: 600 }}>
                                {formatPct(d.profitPct)}
                              </span>
                            ) : '--'}
                          </TableCell>
                          {/* 今日行情 */}
                          <TableCell align="right">
                            {d.todayPrice !== undefined ? (
                              <span>
                                {formatNumber(d.todayPrice)}
                                <Typography component="span" variant="caption" sx={{ display: 'block', color: getPriceColor(d.todayChangePct ?? 0) }}>
                                  {formatPct(d.todayChangePct)}
                                </Typography>
                              </span>
                            ) : '--'}
                          </TableCell>
                          {/* 当时入选条件 */}
                          <TableCell>
                            {d.matchedRules.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {d.matchedRules.map((r, i) => (
                                  <Typography key={i} variant="caption" color="text.secondary">
                                    • {r}
                                  </Typography>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">--</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* 每日盈亏记录 */}
          {dailyPnlList.length > 0 && (
            <Paper sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
                每日盈亏记录（近{dailyPnlList.length}天）
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>日期</TableCell>
                      <TableCell align="center">买入</TableCell>
                      <TableCell align="center">卖出</TableCell>
                      <TableCell align="center">持仓数</TableCell>
                      <TableCell align="right">已实现盈亏</TableCell>
                      <TableCell align="right">浮动盈亏</TableCell>
                      <TableCell align="right">总资产</TableCell>
                      <TableCell align="right">收益率</TableCell>
                      <TableCell>备注</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailyPnlList.map((d) => {
                      // 本金从账户实时取，切勿硬编码（2026-09-30：本金可设定，写死会算错收益率）
                      const initialCapital = account?.initialCapital || 1;
                      const profitPct = ((d.totalAssets - initialCapital) / initialCapital) * 100;
                      return (
                        <TableRow key={d.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{d.date}</TableCell>
                          <TableCell align="center">{d.buyCount > 0 ? d.buyCount : '-'}</TableCell>
                          <TableCell align="center">{d.sellCount > 0 ? d.sellCount : '-'}</TableCell>
                          <TableCell align="center">{d.heldCount}</TableCell>
                          <TableCell align="right">
                            {d.realizedProfit !== 0 ? (
                              <span style={{ color: getPriceColor(d.realizedProfit), fontWeight: 600 }}>
                                {formatMoney(d.realizedProfit)}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {d.unrealizedProfit !== 0 ? (
                              <span style={{ color: getPriceColor(d.unrealizedProfit), fontWeight: 600 }}>
                                {formatMoney(d.unrealizedProfit)}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right">{formatMoney(d.totalAssets)}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: getPriceColor(profitPct), fontWeight: 600 }}>
                              {formatPct(profitPct)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {d.note === 'auto_trade' ? '自动交易' : d.note === 'no_trade' ? '无交易' : d.note}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      ) : (
        <Typography color="text.secondary">暂无数据</Typography>
      )}

      {/* 本金设定对话框 —— 危险操作，需显式确认 */}
      <Dialog open={capitalOpen} onClose={() => setCapitalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>设定本金</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            此操作会<strong>清空全部持仓、成交记录、资金流水与每日盈亏</strong>，并把本金与余额都设为新值。
            当前持仓会被直接销账（不按市价卖出），请确认后再执行。
          </Alert>
          <TextField
            label="本金金额（元）"
            type="number"
            fullWidth
            value={capitalInput}
            onChange={(e) => setCapitalInput(e.target.value)}
            inputProps={{ min: 1000, step: 10000 }}
            helperText={
              account
                ? `当前本金 ${account.initialCapital.toLocaleString('zh-CN')} 元，余额 ${account.balance.toLocaleString('zh-CN')} 元`
                : '读取中…'
            }
          />
          {capitalInput && Number(capitalInput) > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              建议同步把「持仓总市值上限」设为相同或相近档位，否则每笔金额过大时会被上限截断、买不满每日只数。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCapitalOpen(false)}>取消</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={savingCapital || !(Number(capitalInput) > 0)}
            onClick={async () => {
              setSavingCapital(true);
              try {
                const acc = await setInitialCapital(Number(capitalInput));
                setAccount(acc);
                setCapitalOpen(false);
                // 清了数据，重新拉总结与每日盈亏
                const data = await fetchSummary(period);
                setSummary(data);
                fetchDailyPnl(30).then(setDailyPnlList).catch(() => {});
              } catch (e: any) {
                alert(e?.response?.data?.message || e?.message || '设定本金失败');
              } finally {
                setSavingCapital(false);
              }
            }}
          >
            {savingCapital ? '执行中…' : '确认清空并设定'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
